import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import {
  adminListArticles, adminCreateArticle, adminUpdateArticle, adminDeleteArticle,
  adminUploadImage,
} from "@/lib/api";
import AdminHeader from "@/components/admin/AdminHeader";
import RichTextEditor from "@/components/articles/RichTextEditor";

const slugify = (s) => (s || "").toLowerCase().trim()
  .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

const assetUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const emptyForm = {
  id: null, title: "", slug: "", category: "",
  featured_image: null, excerpt: "", content: "",
  seo_title: "", meta_description: "",
  featured: false, status: "draft", publish_date: "",
};

export default function AdminArticlesPage() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoadingItems(true);
    try { setItems(await adminListArticles()); }
    catch { toast.error("Failed to load articles"); }
    setLoadingItems(false);
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
    else if (user) load();
  }, [user, loading, navigate, load]);

  const startCreate = () => setEditing({ ...emptyForm });
  const startEdit = (a) => setEditing({
    ...emptyForm, ...a,
    publish_date: a.publish_date ? a.publish_date.slice(0, 10) : "",
    seo_title: a.seo_title || "", meta_description: a.meta_description || "",
    category: a.category || "",
  });
  const cancel = () => setEditing(null);

  const doSave = async (statusOverride) => {
    if (!editing.title.trim()) { toast.error("Article title required"); return; }
    const payload = {
      title: editing.title.trim(),
      slug: (editing.slug || slugify(editing.title)).trim(),
      category: editing.category || "",
      featured_image: editing.featured_image || null,
      excerpt: editing.excerpt || "",
      content: editing.content || "",
      seo_title: editing.seo_title || null,
      meta_description: editing.meta_description || null,
      featured: !!editing.featured,
      status: statusOverride || editing.status || "draft",
      publish_date: editing.publish_date
        ? new Date(editing.publish_date).toISOString()
        : null,
    };
    setSaving(true);
    try {
      if (editing.id) await adminUpdateArticle(editing.id, payload);
      else await adminCreateArticle(payload);
      toast.success(editing.id ? "Article updated" : "Article created");
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const onDelete = async (a) => {
    if (!window.confirm(`Delete article "${a.title}"?`)) return;
    try { await adminDeleteArticle(a.id); toast.success("Article deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const preview = (a) => window.open(`/resources/${a.slug}`, "_blank");

  const uploadCover = async (file) => {
    if (!file) return;
    try {
      const { url } = await adminUploadImage(file);
      setEditing((prev) => ({ ...prev, featured_image: url }));
    } catch { toast.error(`Upload failed`); }
  };

  if (loading || !user) return <div className="admin-shell"><div className="admin-loading">Loading…</div></div>;

  return (
    <div className="admin-shell utsah-app" data-testid="admin-articles-page">
      <AdminHeader active="articles" onLogout={async () => { await logout(); navigate("/admin/login"); }} email={user.email} />
      <div className="admin-container">
        <div className="admin-heading-row">
          <div>
            <div className="eyebrow">Content</div>
            <h1 className="admin-heading">Articles</h1>
          </div>
          {!editing && (
            <button className="admin-export-btn" onClick={startCreate} data-testid="admin-new-article-btn">+ New Article</button>
          )}
        </div>

        {editing ? (
          <ArticleForm
            form={editing}
            saving={saving}
            onChange={setEditing}
            onSave={doSave}
            onCancel={cancel}
            onPreview={editing.id ? () => preview(editing) : null}
            onUploadCover={uploadCover}
          />
        ) : (
          <div className="admin-table-wrap" data-testid="admin-articles-table">
            {loadingItems ? (
              <div className="admin-loading">Loading…</div>
            ) : items.length === 0 ? (
              <div className="admin-empty">No articles yet. Click "New Article" to create one.</div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr><th>Title</th><th>Category</th><th>Publish Date</th><th>Featured</th><th>Status</th><th className="admin-th-actions">Actions</th></tr>
                </thead>
                <tbody>
                  {items.map((a) => (
                    <tr key={a.id} data-testid={`art-row-${a.slug}`}>
                      <td><strong>{a.title}</strong><div style={{ fontSize: 11, opacity: 0.5 }}>{a.slug}</div></td>
                      <td>{a.category || <span style={{ opacity: 0.4 }}>—</span>}</td>
                      <td>{a.publish_date ? a.publish_date.slice(0, 10) : <span style={{ opacity: 0.4 }}>—</span>}</td>
                      <td>{a.featured ? "Yes" : "—"}</td>
                      <td><span className={`admin-badge admin-badge-${a.status}`}>{a.status}</span></td>
                      <td className="admin-td-actions">
                        <button className="admin-link-btn" onClick={() => startEdit(a)} data-testid={`art-edit-${a.slug}`}>Edit</button>
                        <button className="admin-link-btn" onClick={() => preview(a)} data-testid={`art-preview-${a.slug}`}>Preview</button>
                        <button className="admin-link-btn admin-link-danger" onClick={() => onDelete(a)} data-testid={`art-del-${a.slug}`}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      <Toaster position="bottom-center" richColors theme="light" />
    </div>
  );
}

function ArticleForm({ form, saving, onChange, onSave, onCancel, onPreview, onUploadCover }) {
  const set = (k, v) => onChange({ ...form, [k]: v });
  const excerptLen = (form.excerpt || "").length;

  return (
    <div className="admin-form" data-testid="admin-article-form">
      <FormSection title="1 · Basic Information">
        <div className="admin-form-row">
          <label>Title<span className="req">*</span></label>
          <input type="text" value={form.title}
            onChange={(e) => set("title", e.target.value)}
            onBlur={() => { if (!form.slug) set("slug", slugify(form.title)); }}
            data-testid="art-form-title" />
        </div>
        <div className="admin-form-row">
          <label>URL Slug</label>
          <input type="text" value={form.slug}
            onChange={(e) => set("slug", slugify(e.target.value))}
            placeholder="auto from title" data-testid="art-form-slug" />
          <div className="admin-form-hint">Public URL: /resources/{form.slug || "your-slug"}</div>
        </div>
        <div className="admin-form-row">
          <label>Category</label>
          <input type="text" value={form.category}
            onChange={(e) => set("category", e.target.value)}
            placeholder="e.g. Gifting Ideas, HR Guides, Sustainability"
            data-testid="art-form-category" />
        </div>
        <div className="admin-form-row">
          <label>Featured Image (Cover)</label>
          <input type="file" accept="image/*"
            onChange={(e) => onUploadCover(e.target.files?.[0])}
            data-testid="art-form-cover-upload" />
          {form.featured_image && (
            <div className="admin-image-tile" style={{ maxWidth: 240, marginTop: 12 }}>
              <img src={assetUrl(form.featured_image)} alt="" />
              <div className="admin-image-controls">
                <button type="button" className="admin-link-danger"
                  onClick={() => set("featured_image", null)}>Remove</button>
              </div>
            </div>
          )}
        </div>
        <div className="admin-form-row">
          <label>Short Excerpt (150–200 chars)</label>
          <textarea rows={2} value={form.excerpt}
            onChange={(e) => set("excerpt", e.target.value.slice(0, 400))}
            data-testid="art-form-excerpt" />
          <div className="admin-form-hint">{excerptLen} / recommended 200 characters</div>
        </div>
      </FormSection>

      <FormSection title="2 · Article Content">
        <RichTextEditor value={form.content} onChange={(html) => set("content", html)} />
      </FormSection>

      <FormSection title="3 · SEO (Optional)">
        <div className="admin-form-row">
          <label>SEO Title</label>
          <input type="text" value={form.seo_title}
            onChange={(e) => set("seo_title", e.target.value)}
            placeholder={`Auto: ${form.title || "Article Title"} | Utsah`}
            data-testid="art-form-seo-title" />
        </div>
        <div className="admin-form-row">
          <label>Meta Description</label>
          <textarea rows={2} value={form.meta_description}
            onChange={(e) => set("meta_description", e.target.value)}
            placeholder={`Auto: ${form.excerpt?.slice(0, 140) || "Uses excerpt if left blank"}`}
            data-testid="art-form-meta-desc" />
        </div>
      </FormSection>

      <FormSection title="4 · Publishing">
        <div className="admin-form-row">
          <label>Featured</label>
          <label className="admin-chip">
            <input type="checkbox" checked={!!form.featured}
              onChange={(e) => set("featured", e.target.checked)}
              data-testid="art-form-featured" />
            Feature this article
          </label>
        </div>
        <div className="admin-form-row">
          <label>Publish Date</label>
          <input type="date" value={form.publish_date || ""}
            onChange={(e) => set("publish_date", e.target.value)}
            data-testid="art-form-publish-date" />
          <div className="admin-form-hint">Leave blank to auto-fill when publishing.</div>
        </div>
        <div className="admin-form-row">
          <label>Status</label>
          <select value={form.status}
            onChange={(e) => set("status", e.target.value)}
            data-testid="art-form-status">
            <option value="draft">Draft (hidden)</option>
            <option value="published">Published (live)</option>
          </select>
        </div>
      </FormSection>

      <div className="admin-form-actions" data-testid="art-form-actions">
        <button className="admin-nav-btn" onClick={onCancel} disabled={saving} data-testid="art-form-cancel">Cancel</button>
        {onPreview && (
          <button className="admin-nav-btn" onClick={onPreview} disabled={saving} data-testid="art-form-preview">Preview</button>
        )}
        <button className="admin-nav-btn" onClick={() => onSave("draft")} disabled={saving} data-testid="art-form-save-draft">Save Draft</button>
        <button className="admin-export-btn" onClick={() => onSave("published")} disabled={saving} data-testid="art-form-publish">
          {saving ? "Saving…" : "Publish"}
        </button>
      </div>
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <section className="admin-form-section">
      <h3 className="admin-form-section-title">{title}</h3>
      {children}
    </section>
  );
}
